import React, { useCallback, useRef, useState } from "react";
import { ActionItemArchiveIcon } from "./actionItemArchiveIcons";

/**
 * Toggle for session archived view when the Archived stat tab is hidden.
 *
 * @example
 * // Input — viewing archived without the settings tab enabled
 * <ActionItemsArchiveHoverMenu
 *   archivedActive
 *   onEnterArchivedView={() => setActiveFilter("ignored")}
 *   onExitArchivedView={() => setActiveFilter("pending")}
 * />
 * // Output — archive icon with active dot; hover shows tooltip, click toggles view
 */
const ActionItemsArchiveHoverMenu = ({
  archivedActive = false,
  onEnterArchivedView,
  onExitArchivedView,
  menuLabel = "Archived tasks",
}) => {
  const buttonRef = useRef(null);
  const [suppressTooltip, setSuppressTooltip] = useState(false);

  const handleToggle = useCallback(() => {
    if (archivedActive) {
      onExitArchivedView?.();
    } else {
      onEnterArchivedView?.();
    }
    setSuppressTooltip(true);
    buttonRef.current?.blur();
  }, [archivedActive, onEnterArchivedView, onExitArchivedView]);

  const rootClass = [
    "action-items-archive-toggle",
    suppressTooltip ? "action-items-archive-toggle--suppress-tooltip" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const tooltip = archivedActive ? "Back to main view" : "View archived tasks";
  const ariaLabel = archivedActive ? `${menuLabel} (viewing archived)` : menuLabel;

  const toggleClass = [
    "action-items-archive-hover-menu__toggle",
    "action-items-header-icon-btn",
    archivedActive ? "action-items-archive-hover-menu__toggle--active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={rootClass}
      onMouseLeave={() => setSuppressTooltip(false)}
    >
      <button
        ref={buttonRef}
        type="button"
        className={toggleClass}
        aria-pressed={archivedActive}
        aria-label={ariaLabel}
        onClick={handleToggle}
      >
        <ActionItemArchiveIcon size={16} />
      </button>
      <span className="action-items-header-icon-tooltip" role="tooltip">
        {tooltip}
      </span>
    </div>
  );
};

export default ActionItemsArchiveHoverMenu;
